/**
 * Seam between a review-collection API and the local persisted store.
 *
 * - false (default, and the only working value today): the settings live
 *   entirely in `useReviewInviteStore`, no network calls. They survive a reload
 *   because the store persists; nothing is shared between devices or staff
 *   members, and nothing reaches the guest-facing app.
 * - true: `useReviewInviteQuery` would hydrate the store from the endpoint
 *   below and every save would mirror back. Components do not change between
 *   the two modes, which is the whole point of the seam. Same arrangement as
 *   the loyalty programme, the floor planner and the roster.
 *
 * WHY IT IS OFF: core-api keeps the guests' reviews (`domain/reviews`) but
 * not these settings: there is no route that stores them or hands them to the
 * guest app, so the page below configures a flow nothing can yet run.
 *
 * WHAT THE BACKEND WOULD NEED, in the order it becomes useful:
 *
 * 1. The settings themselves, one document per restaurant, addressed like the
 *    floor plan is: GET/PUT `/restaurants/{restaurantId}/review-invite`. The
 *    body is the whole document and PUT replaces it, which is the shape this
 *    store already has.
 *
 * 2. The guest side has to read them, and it is a different app: the
 *    invitation is shown by `apps/guest-web` after payment. That read has no
 *    session, so it needs a public route the way the mobile-app theme already
 *    has one. guest-web already calls it, behind its `USE_MOCK_REVIEW_INVITE`
 *    flag: GET `/public/restaurants/{restaurantId}/review-invite` answering
 *    `{ isActive, googleFromStars, reviewUrl }`, the last being the Google
 *    connection's link or null. Without it the setting is saved here and
 *    never seen by the only party it describes.
 *
 * 3. The stars a guest gives now have a home, POST
 *    `/public/restaurants/{restaurantId}/reviews` and GET
 *    `/restaurants/{restaurantId}/reviews`; the tiles on the right are still
 *    placeholder data until this page reads the latter.
 *
 * THE VENUE ON GOOGLE COMES FROM SETTINGS: the review link is the one the
 * venue's Google connection carries (`features/google-location`), so this page
 * picks nothing and stores no place of its own.
 *
 * EVERY GUEST GETS THE LINK: the stars decide what a guest sees first, never
 * who is sent to Google. From `googleFromStars` up the Google review is the
 * offer; below it the guest is first asked what went wrong, for the venue
 * alone, and the same Google link stays on that screen, in plain sight.
 * Showing it only to happy guests would be review gating, which Google's
 * policy prohibits ("selectively solicit positive reviews") and which lands
 * on the restaurant's listing.
 */
export const REVIEW_INVITE_USE_API = false

/** Backend path, relative to `/restaurants/{restaurantId}`. */
export const REVIEW_INVITE_ENDPOINT = '/review-invite'

/**
 * Bumped when the persisted shape changes.
 *
 * 1: initial shape.
 * 2: `rules.googleReviewUrl` replaced by `rules.place`, the venue picked out
 *    of Google's own suggestions.
 * 3: `rules.mode` dropped. The star threshold says the same thing on its own.
 * 4: `rules` dropped: the threshold, because every guest gets the link, and
 *    the place, because the link comes from the Google connection in Settings.
 *    That connection lives on the server, which an upgrade in the browser
 *    cannot write to, so a venue that picked its place here connects it once
 *    in Settings. Only dev ever had the picker: `main` has no version of this
 *    store at all.
 * 5: `googleFromStars` added. It is the old threshold back with a different
 *    meaning (what a guest sees first, not who goes to Google), so a venue
 *    that had set one before v4 gets its number back.
 *
 * A bumped version with no `migrate` makes zustand discard the whole payload,
 * which here would take the on/off switch with it, and there is no backend to
 * restore it from.
 */
export const REVIEW_INVITE_SCHEMA_VERSION = 5

/** The guest app's scale: a guest rates the visit in whole stars, 1 to 5. */
export const REVIEW_STARS = 5

/**
 * Four and five go straight to the Google review, the rest are first asked
 * what went wrong.
 */
export const DEFAULT_GOOGLE_FROM_STARS = 4
